import { Injectable, InjectionToken } from '@angular/core';
import { Action } from '@ngrx/store';
import { autofillCheckpoints, updateCheckpoint, updateCheckpoints } from '../store/actions/checkpoint.actions';
import { updateCustomerIdentification } from '../store/actions/customer-identification.actions';
import { cleanupFormListSuccess, loadFormList } from '../store/actions/form-list.actions';
import {
    abortForm,
    addOrUpdateParticipantsfromConfirmation,
    addParticipantCopy,
    collapseAccordionByGroup,
    expandAccordionByGroup,
    generateChecklistCopies,
    loadForm,
    loadFormError,
    loadFormIntoCache,
    loadFormIntoCacheSuccess,
    loadFormSuccess,
    refreshXML,
    refreshXMLSuccess,
    removeParticipant,
    removeParticipantFromConfirmationSuccess,
    saveParticipantParticulars,
    saveRecipients,
    selectParticipant,
    submitCurrentForm,
    submitFormSuccess,
    updateCurrentlyEditedParticipant,
    updatedXml,
    updateSignFieldSuccess,
} from '../store/actions/form.actions';
import {
    autofillFunctionalElements,
    updateAstoriaOption,
    updateCosimaOption,
    updateDatePerformed,
    updateDescription,
    updateDiscrepancyLog,
    updateFunctionalElementRemarks,
    updateFunctionalElementSelection,
    updateMaterialNumber,
    updateReplacementOrLastProcedureDateSelection,
    updateReplacementOrLastProcedureDateValue,
    updateSerialNumber,
    updateSignature,
    updateSignatureName,
    updateSystemConditionSignature,
    updateSystemConditionSignatureName,
    updateValue,
} from '../store/actions/functional-elements.actions';
import {
    deleteLastOpenedForm,
    restoreLastOpenedForm,
    tryToPerformPaceFormsQuickAccess,
    updateLastOpenedForm,
} from '../store/actions/last-opened-form.actions';
import {
    cleanupSpqDocs,
    loadSpqDoc,
    loadSpqDocSuccess,
    submitSpqDocSuccess,
    updateDefectServicePartIdentificationField,
} from '../store/actions/questionnaire.actions';
import {
    updateSystemConditionDeficienciesSelection,
    updateSystemConditionRemarks,
} from '../store/actions/system-condition.actions';

export interface ActionUtilitiesInterface {
    getLogString(action: Action): string;
}
export const ActionUtilitiesInstanceToken = new InjectionToken<ActionUtilitiesInterface>('ActionUtilitiesInterface');

@Injectable({ providedIn: 'root' })
export class ActionUtilitiesService implements ActionUtilitiesInterface {
    public getLogString(action: Action): string {
        let typedAction;
        let tmpString: string; // for usage in building specialized log string
        switch (action.type) {
            case loadForm.type:
                typedAction = action as ReturnType<typeof loadForm>;
                return `${action.type}; form ${typedAction.form.formId} of type ${typedAction.formType} on notification ${typedAction.notification.qmNum}:${typedAction.notification.maNum} for language '${typedAction.language}'`;
            case loadFormSuccess.type:
                typedAction = action as ReturnType<typeof loadFormSuccess>;
                return `${action.type}; form ${typedAction.form.formId}`;
            case loadFormError.type:
                typedAction = action as ReturnType<typeof loadFormError>;
                return `${action.type}; form ${typedAction.form.formId} on notification ${typedAction.notification.qmNum}:${typedAction.notification.maNum} with error '${typedAction.error}'`;
            case updatedXml.type:
                typedAction = action as ReturnType<typeof updatedXml>;
                return `${action.type}; id ${typedAction.participant}`;
            case abortForm.type:
                typedAction = action as ReturnType<typeof abortForm>;
                return `${action.type}; form ${typedAction.form.formId} on notification ${typedAction.notification.qmNum}:${typedAction.notification.maNum}`;
            case refreshXML.type:
                typedAction = action as ReturnType<typeof refreshXML>;
                return `${action.type}; form ${typedAction.form.formId}, mainEquipment: ${typedAction.mainEquipment} on notification ${typedAction.notification.qmNum}:${typedAction.notification.maNum}, fromCache: ${typedAction.fromCache}`;
            case refreshXMLSuccess.type:
                typedAction = action as ReturnType<typeof refreshXMLSuccess>;
                return `${action.type}; form ${typedAction.form.formId}, mainEquipment: ${typedAction.mainEquipment} on notification ${typedAction.notification.qmNum}:${typedAction.notification.maNum}, fromCache: ${typedAction.fromCache}`;
            case submitCurrentForm.type:
                typedAction = action as ReturnType<typeof submitCurrentForm>;
                return `${action.type}; id ${typedAction.id} in save mode '${typedAction.saveMode}'`;
            case submitFormSuccess.type:
                typedAction = action as ReturnType<typeof submitFormSuccess>;
                return `${action.type}; notification: ${typedAction.notification.qmNum}:${
                    typedAction.notification.maNum
                }, ids: ${typedAction.ids.join(' ')}`;
            case loadFormList.type:
                typedAction = action as ReturnType<typeof loadFormList>;
                return `${action.type}; material ${typedAction.mainEquipment.matnr}, equipment ${typedAction.equipmentNr}, fromCache: ${typedAction.fromCache} on notification ${typedAction.notification.qmNum}:${typedAction.notification.maNum}`;
            case cleanupFormListSuccess.type:
                typedAction = action as ReturnType<typeof cleanupFormListSuccess>;
                return `${action.type}; notification ${typedAction.notification.qmNum}:${typedAction.notification.maNum}`;
            case loadFormIntoCache.type:
                typedAction = action as ReturnType<typeof loadFormIntoCache>;
                return `${action.type}; form ${typedAction.form.formId} on notification ${typedAction.notification.qmNum}:${typedAction.notification.maNum} in status ${typedAction.status}`;
            case loadFormIntoCacheSuccess.type:
                typedAction = action as ReturnType<typeof loadFormIntoCacheSuccess>;
                return `${action.type}; form ${typedAction.form.formId} on notification ${typedAction.notification.qmNum}:${typedAction.notification.maNum}; locked by ${typedAction.lockedBy}`;
            case saveRecipients.type:
                typedAction = action as ReturnType<typeof saveRecipients>;
                return `${action.type}; recipients: '${JSON.stringify(typedAction.recipients)}'`;
            case selectParticipant.type:
                typedAction = action as ReturnType<typeof selectParticipant>;
                return `${action.type}; id ${typedAction.id}`;
            case removeParticipant.type:
                typedAction = action as ReturnType<typeof removeParticipant>;
                return `${action.type}; id ${typedAction.participant}`;
            case removeParticipantFromConfirmationSuccess.type:
                typedAction = action as ReturnType<typeof removeParticipantFromConfirmationSuccess>;
                return `${action.type}; id ${typedAction.participant}`;
            case generateChecklistCopies.type:
                typedAction = action as ReturnType<typeof generateChecklistCopies>;
                return `${action.type}; ids ${typedAction.selectedParticipants.join(' ')}`;
            case addOrUpdateParticipantsfromConfirmation.type:
                typedAction = action as ReturnType<typeof addOrUpdateParticipantsfromConfirmation>;
                return `${action.type}; participants ${typedAction.participants
                    .map((p) => `"${p.firstName} ${p.lastName}"`)
                    .join(' ')}`;
            case addParticipantCopy.type:
                typedAction = action as ReturnType<typeof addParticipantCopy>;
                return `${action.type}; id ${typedAction.id}`;
            case saveParticipantParticulars.type:
                typedAction = action as ReturnType<typeof saveParticipantParticulars>;
                return `${action.type}; participant ${JSON.stringify(typedAction.participant)}`;
            case updateCurrentlyEditedParticipant.type:
                typedAction = action as ReturnType<typeof updateCurrentlyEditedParticipant>;
                return `${action.type}; id ${typedAction.id}`;
            case updateFunctionalElementSelection.type:
                typedAction = action as ReturnType<typeof updateFunctionalElementSelection>;
                return `${action.type}; id ${typedAction.id}, selectedChoiceXmlId: '${typedAction.selectedChoiceXmlId}'`;
            case updateDiscrepancyLog.type:
                typedAction = action as ReturnType<typeof updateDiscrepancyLog>;
                return `${action.type}; id ${typedAction.id}, xmlId: '${typedAction.xmlId}', newValue: '${typedAction.newValue}'`;
            case updateSignature.type:
                typedAction = action as ReturnType<typeof updateSignature>;
                return `${action.type}; id ${typedAction.id}, signature type: '${typedAction.signatureType}', dateXmlId: '${typedAction.dateXmlId}', date: '${typedAction.date}'`;
            case updateSignatureName.type:
                typedAction = action as ReturnType<typeof updateSignatureName>;
                return `${action.type}; id ${typedAction.id}, signature type: '${typedAction.signatureType}', xmlId: '${typedAction.xmlId}', firstName: '${typedAction.firstName}', lastName: '${typedAction.lastName}', email: '${typedAction.email}'`;
            case updateSystemConditionSignature.type:
                typedAction = action as ReturnType<typeof updateSystemConditionSignature>;
                return `${action.type}; signature type: '${typedAction.signatureType}', dateXmlId: '${typedAction.dateXmlId}', date: '${typedAction.date}'`;
            case updateSystemConditionSignatureName.type:
                typedAction = action as ReturnType<typeof updateSystemConditionSignatureName>;
                return `${action.type}; signature type: '${typedAction.signatureType}', xmlId: '${typedAction.xmlId}', firstName: '${typedAction.firstName}', lastName: '${typedAction.lastName}'`;
            case updateSignFieldSuccess.type:
                typedAction = action as ReturnType<typeof updateSignFieldSuccess>;
                return `${action.type}`;
            case updateMaterialNumber.type:
            case updateSerialNumber.type:
            case updateDescription.type:
                typedAction = action as ReturnType<typeof updateMaterialNumber>;
                return `${action.type}; id ${typedAction.id}, xmlId: '${typedAction.xmlId}', newValue: '${typedAction.newValue}'`;
            case updateValue.type:
                typedAction = action as ReturnType<typeof updateValue>;
                return `${action.type}; id ${typedAction.id}, xmlId: '${typedAction.xmlId}', newValue: '${typedAction.newValue}'`;
            case updateDatePerformed.type:
            case updateReplacementOrLastProcedureDateValue.type:
                typedAction = action as ReturnType<typeof updateDatePerformed>;
                if (typedAction.dayXmlId && typedAction.monthXmlId && typedAction.yearXmlId) {
                    return `${action.type}; id ${typedAction.id}, dayXmlId: '${typedAction.dayXmlId}', monthXmlId: '${typedAction.monthXmlId}', yearXmlId: '${typedAction.yearXmlId}', newValue: '${typedAction.newValue}'`;
                }
                return `${action.type}; id ${typedAction.id}, newValue: '${typedAction.newValue}'`;
            case updateReplacementOrLastProcedureDateSelection.type:
                typedAction = action as ReturnType<typeof updateReplacementOrLastProcedureDateSelection>;
                return `${action.type}; id ${typedAction.id}, xmlId: '${typedAction.xmlId}', selected: '${typedAction.selected}'`;
            case updateFunctionalElementRemarks.type:
                typedAction = action as ReturnType<typeof updateFunctionalElementRemarks>;
                return `${action.type}; id ${typedAction.id}, xmlId: '${typedAction.xmlId}', remarks: '${typedAction.newValue}'`;
            case updateCheckpoint.type:
                typedAction = action as ReturnType<typeof updateCheckpoint>;
                return `${action.type}; xmlId: '${typedAction.xmlId}', selected: '${typedAction.selected}'`;
            case updateCheckpoints.type:
                typedAction = action as ReturnType<typeof updateCheckpoints>;
                return `${action.type}; xmlIds: '${typedAction.xmlIds}', selected: '${typedAction.selected}'`;
            case autofillCheckpoints.type:
                typedAction = action as ReturnType<typeof autofillCheckpoints>;
                return `${action.type}; id: '${typedAction.id}', selected: '${typedAction.selected}'`;
            case updateCustomerIdentification.type:
                typedAction = action as ReturnType<typeof updateCustomerIdentification>;
                return `${action.type}; xmlId: '${typedAction.xmlId}', newValue: '${typedAction.newValue}, invalidateSignatures: '${typedAction.invalidateSignatures}'`;
            case updateSystemConditionDeficienciesSelection.type:
                typedAction = action as ReturnType<typeof updateSystemConditionDeficienciesSelection>;
                return `${action.type}; updating deficiencies selection to '${typedAction.selected}' with xmlId '${typedAction.xmlId}'`;
            case updateSystemConditionRemarks.type:
                typedAction = action as ReturnType<typeof updateSystemConditionRemarks>;
                return `${action.type}; updating system condition remarks to '${typedAction.newValue}' with xmlId '${typedAction.xmlId}'`;
            case expandAccordionByGroup.type:
                typedAction = action as ReturnType<typeof expandAccordionByGroup>;
                return `${action.type}; groupId: '${typedAction.groupId}'`;
            case collapseAccordionByGroup.type:
                typedAction = action as ReturnType<typeof collapseAccordionByGroup>;
                typedAction = action as ReturnType<typeof collapseAccordionByGroup>;
                return `${action.type}; groupId: '${typedAction.groupId}'`;
            case tryToPerformPaceFormsQuickAccess.type:
                typedAction = action as ReturnType<typeof tryToPerformPaceFormsQuickAccess>;
                return `${action.type}; notification: ${typedAction.notification.qmNum}:${typedAction.notification.maNum}, equipmentId: ${typedAction.equipmentId}, materialNr: ${typedAction.materialNr}, serialNr: ${typedAction.serialNr}`;
            case updateLastOpenedForm.type:
                typedAction = action as ReturnType<typeof updateLastOpenedForm>;
                return `${action.type}; notification: ${typedAction.notification.qmNum}:${typedAction.notification.maNum}, form: ${typedAction.form.formId}`;
            case deleteLastOpenedForm.type:
                typedAction = action as ReturnType<typeof deleteLastOpenedForm>;
                return `${action.type}; notification: ${typedAction.notification.qmNum}:${typedAction.notification.maNum}`;
            case restoreLastOpenedForm.type:
                typedAction = action as ReturnType<typeof restoreLastOpenedForm>;
                return `${action.type}; lastOpenedForm: '${JSON.stringify(typedAction.lastOpenedForm)}'`;
            case updateAstoriaOption.type:
                typedAction = action as ReturnType<typeof updateAstoriaOption>;
                return `${action.type}; xmlId: '${typedAction.xmlId}', selected: '${typedAction.selected}'`;
            case updateCosimaOption.type:
                typedAction = action as ReturnType<typeof updateCosimaOption>;
                return `${action.type}; selected: '${typedAction.selectedChoiceXmlId}'`;
            case autofillFunctionalElements.type:
                typedAction = action as ReturnType<typeof autofillFunctionalElements>;
                return `${action.type}; id: '${typedAction.id}', selected: '${typedAction.selected}'`;
            case loadSpqDoc.type:
                typedAction = action as ReturnType<typeof loadSpqDoc>;
                return `${action.type}; notification: ${typedAction.notification.qmNum}:${typedAction.notification.maNum}, form: ${typedAction.form.formId}, material: ${typedAction.materialKey}, language: ${typedAction.language}'`;
            case loadSpqDocSuccess.type:
                typedAction = action as ReturnType<typeof loadSpqDocSuccess>;
                return `${action.type}; notification: ${typedAction.notification.qmNum}:${typedAction.notification.maNum}, form: ${typedAction.form.formId}, material: ${typedAction.materialKey}, language: ${typedAction.language}'`;
            case submitSpqDocSuccess.type:
                typedAction = action as ReturnType<typeof submitSpqDocSuccess>;
                return `${action.type}; notification: ${typedAction.notification.qmNum}:${typedAction.notification.maNum}, form: ${typedAction.form.formId}, saveMode: ${typedAction.saveMode}'`;
            case cleanupSpqDocs.type:
                typedAction = action as ReturnType<typeof cleanupSpqDocs>;
                tmpString = typedAction.spqMaterials.reduce((curr, m) => {
                    const notificationString = `notification: ${m.notificationId.qmNum}:${m.notificationId.maNum}`;
                    const formString = `form: ${m.form.formId}`;
                    const materialString = `material: ${m.materialKey}`;
                    return curr + ' ' + notificationString + ' ' + formString + ' ' + materialString;
                }, '');
                return `${action.type}; materials: ${tmpString}'`;
            case updateDefectServicePartIdentificationField.type:
                typedAction = action as ReturnType<typeof updateDefectServicePartIdentificationField>;
                return `${action.type}; xmlId: ${typedAction.xmlId}, value: '${typedAction.newValue}'`;
            default:
                return action.type;
        }
    }
}

export function isUpdateCosimaOptionAction(action: Action): action is ReturnType<typeof updateCosimaOption> {
    return action.type === updateCosimaOption.type;
}

export function isUpdateAstoriaOptionAction(action: Action): action is ReturnType<typeof updateAstoriaOption> {
    return action.type === updateAstoriaOption.type;
}
